import { SxProps, Theme } from '@mui/material';
import { isValidPhoneNumber, parsePhoneNumber } from 'libphonenumber-js';
import {
  addDays,
  addMilliseconds,
  format,
  differenceInYears,
  differenceInSeconds,
  differenceInMinutes,
  differenceInHours,
  differenceInDays,
} from 'date-fns';
import { formatInTimeZone, getTimezoneOffset } from 'date-fns-tz';
import { Roles, Users } from '@prisma/client';
import * as yup from 'yup';
import { toast } from 'react-toastify';
import {
  Appointment,
  Employee,
  EmployeeToBusiness,
  GeneralRequest,
  LoadedUser,
  Trip,
} from '../../interfaces';
import { ShortDay } from '../../types/employees';
import { getFullName } from '../../helpers/global';
import {
  DEFAULT_TIMEZONE,
  USE_DEFAULT_TIMEZONE_FOR_DATELINE_CHECK,
} from '../../constants/appointments';
import { QueueAppointment } from '../../types/queue';

export function getCleanedPhone(phoneNumber: string) {
  const cleanedPhone = `${phoneNumber}`.replace(/\D/g, '');
  const cleanedMatch = cleanedPhone.match(/^(1|)?(\d{3})(\d{3})(\d{4})$/);
  let finalPhoneNumber = phoneNumber;
  if (cleanedMatch) {
    const intlCode = cleanedMatch[1] ? '+1 ' : '';
    finalPhoneNumber = [
      intlCode,
      '(',
      cleanedMatch[2],
      ') ',
      cleanedMatch[3],
      '-',
      cleanedMatch[4],
    ].join('');
  }
  return finalPhoneNumber;
}

export function getCustomerNameFromAppointment(
  appointment: Appointment | GeneralRequest | QueueAppointment,
) {
  if (appointment.customerGuest) {
    return getFullName({
      firstName: appointment.customerGuest.firstName,
      lastName: appointment.customerGuest.lastName,
    });
  }

  if (appointment.isForSomeoneElse) {
    return getFullName({
      firstName: appointment.recipientFirstName,
      lastName: appointment.recipientLastName,
    });
  }

  return getFullName({
    firstName: appointment.customer.firstName,
    lastName: appointment.customer.lastName,
  });
}

export function getEmployeeNameFromAppointment(appointment: Appointment) {
  if (!appointment.employee) {
    if (
      appointment.businessId &&
      appointment.appointmentType !== 'WALK_IN' &&
      appointment.status !== 'QUEUED'
    ) {
      return 'Unassigned';
    }
    return '';
  }
  if (appointment.employee.displayName) return appointment.employee.displayName;
  return getFullName({
    firstName: appointment.employee.firstName,
    lastName: appointment.employee.lastName,
  });
}

export function getEmployeeNameFromAppointmentOrGenRequest(
  appointment: Appointment | GeneralRequest,
  employeesToBusiness?: EmployeeToBusiness[],
) {
  const employeeName =
    'appointmentId' in appointment
      ? getEmployeeNameFromAppointment(appointment as Appointment)
      : !appointment.selectedArtists?.length
      ? 'First available'
      : [
          ...(appointment.selectedArtists?.includes('first-available')
            ? ['First Available']
            : []),
          ...(employeesToBusiness
            ?.filter(
              emp => appointment.selectedArtists?.includes(emp.employeeId),
            )
            .map(emp => emp.displayName) || []),
        ].join(', ');

  return employeeName;
}

export function getAge(dob: string | undefined) {
  if (dob) {
    const date = new Date(dob);
    const age = differenceInYears(new Date(), date);
    return JSON.stringify(age);
  }
  return null;
}

export function getBooleanText(variable: boolean | undefined) {
  if (variable !== undefined) {
    if (variable) {
      return 'Yes';
    }
    return 'No';
  }
  return null;
}

export function getArtistName(employee: Employee) {
  if (!employee) return '';
  let artistName = '';
  if (employee.displayName) {
    artistName = employee.displayName;
  } else if (employee.user) {
    artistName = `${employee.user.firstName} ${employee.user.lastName}`;
  }
  return artistName;
}

export function getInitials({
  firstName,
  lastName,
}: {
  firstName: string | null | undefined;
  lastName: string | null | undefined;
}) {
  return `${firstName?.charAt(0) || ''}${lastName?.charAt(0) || ''}`;
}

export function getArtistInitials(employee: Employee) {
  return getInitials({
    firstName: employee.user?.firstName,
    lastName: employee.user?.lastName,
  });
}

export function getAllTripDates(trips: Trip[]) {
  const newDays: string[] = [];

  trips.forEach(trip => {
    const localTripStart = new Date(trip.startDateTime);
    const localTripEnd = new Date(trip.endDateTime);
    const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const timezoneOffset = getTimezoneOffset(userTimezone, localTripStart);

    let tripStart = addMilliseconds(localTripStart, -1 * timezoneOffset);
    const tripEnd = addMilliseconds(localTripEnd, -1 * timezoneOffset);

    while (tripStart <= tripEnd) {
      const loopDateString = format(tripStart, 'yyyy-MM-dd');
      newDays.push(loopDateString);
      tripStart = addDays(tripStart, 1);
    }
  });

  return newDays;
}

export function getCityTripDates(city: string, trips: Trip[]) {
  const newDays: string[] = [];

  trips.forEach(trip => {
    if (trip.city === city) {
      const localTripStart = new Date(trip.startDateTime);
      const localTripEnd = new Date(trip.endDateTime);
      const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      const timezoneOffset = getTimezoneOffset(userTimezone, localTripStart);

      let tripStart = addMilliseconds(localTripStart, -1 * timezoneOffset);
      const tripEnd = addMilliseconds(localTripEnd, -1 * timezoneOffset);

      while (tripStart <= tripEnd) {
        const loopDateString = format(tripStart, 'yyyy-MM-dd');
        newDays.push(loopDateString);
        tripStart = addDays(tripStart, 1);
      }
    }
  });

  return newDays;
}

export const formatDollarAmount = (
  number: number | undefined,
  omitDecimalsIfZero = false,
  omitDollarSign = false,
) => {
  const dollarSign = omitDollarSign ? '' : '$';

  if (!number) {
    return `${dollarSign}0.00`;
  }

  let newNumber = number;

  // Ensure the input is a number
  if (typeof number !== 'number') {
    newNumber = parseInt(number, 10);
  }

  // The sign leads the whole amount: -$5.00, never $-5.00.
  const sign = newNumber < 0 ? '-' : '';

  // Convert number to a string with exactly two decimal places
  let formattedNumber = Math.abs(newNumber).toFixed(2);

  // Add commas as thousand separators
  const parts = formattedNumber.split('.');
  parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  formattedNumber = parts.join('.');

  if (omitDecimalsIfZero && parts[1] === '00') {
    return `${sign}${dollarSign}${parts[0]}`;
  }

  // Add dollar sign
  return `${sign}${dollarSign}${formattedNumber}`;
};

export function getPriceText(
  priceType: string,
  min?: number,
  max?: number,
  hourly?: number,
  set?: number,
) {
  let text = '';

  switch (priceType) {
    case 'RANGE':
      text = `$${(min ?? 0).toLocaleString('en-US')} - $${(
        max ?? 0
      ).toLocaleString('en-US')}`;
      break;

    case 'HOURLY':
      text = `$${(hourly ?? 0).toLocaleString('en-US')}/hr`;
      break;

    case 'FIXED':
      text = `$${(set ?? 0).toLocaleString('en-US')}`;
      break;

    default:
      break;
  }

  return text;
}

export function isUserAdminOrManager(
  user: Pick<Users | LoadedUser, 'role'> | Roles,
) {
  const role = typeof user === 'string' ? user : user?.role;
  return (['ADMIN', 'SHOP_MANAGER'] as Roles[]).includes(role);
}

export function isUserEmployeeOrAdmin(
  user: Pick<Users | LoadedUser, 'role'> | Roles,
) {
  const role = typeof user === 'string' ? user : user.role;
  return (['ADMIN', 'EMPLOYEE', 'SHOP_MANAGER'] as Roles[]).includes(role);
}

export const isSudoManager = (
  user: Pick<LoadedUser, 'role' | 'canViewReports'>,
) => !!user && isUserAdminOrManager(user) && user.canViewReports;

export const isTeamManager = (
  user: Pick<LoadedUser, 'role' | 'canManageTeam' | 'canViewReports'> & {
    business: { type: string };
  },
) =>
  !!user &&
  user.business.type === 'STUDIO' &&
  (user.canManageTeam || user.canViewReports) &&
  isUserAdminOrManager(user);

// e.g. 90 minutes => 130, 60 minutes => 100, 7 hours 30 minutes => 730, 8 hours => 800
export function convertMinutesToTimeDiff(minutes: number) {
  const hours = Math.floor(minutes / 60);
  const remainingMinutes = minutes % 60;
  return hours * 100 + remainingMinutes;
}

export function getMinutesBetweenOnschedHours(start: number, end: number) {
  const startHours = Math.floor(start / 100);
  const startMinutes = start % 100;
  const endHours = Math.floor(end / 100);
  const endMinutes = end % 100;

  const startTotal = startHours * 60 + startMinutes;
  const endTotal = endHours * 60 + endMinutes;

  return endTotal - startTotal;
}

export const convertMinsToHrsMins = (input: number, short = false) => {
  if (short) {
    if (input < 60) return `${input} min`;
    const hours = Math.floor(input / 60);
    const remaining = input % 60;
    if (remaining === 0) return `${hours} hr`;
    return `${hours} hr ${remaining} min`;
  }
  if (input === 0) return '0 minutes';
  const hours = Math.floor(input / 60).toString();
  let minutes = (input % 60).toString();
  minutes = parseInt(minutes, 10) < 10 ? `0${minutes}` : minutes;
  if (hours === '0') return `${minutes} minutes`;
  if (minutes === '00' && hours === '1') return `${hours} hour`;
  if (minutes === '00') return `${hours} hours`;
  if (hours === '1') return `${hours} hour and ${minutes} minutes`;
  return `${hours} hours and ${minutes} minutes`;
};

export const convertShortDayToFull = (day: ShortDay, plural = false) => {
  let dayString = '';
  switch (day) {
    case 'mon':
      dayString = 'Monday';
      break;
    case 'tue':
      dayString = 'Tuesday';
      break;
    case 'wed':
      dayString = 'Wednesday';
      break;
    case 'thu':
      dayString = 'Thursday';
      break;
    case 'fri':
      dayString = 'Friday';
      break;
    case 'sat':
      dayString = 'Saturday';
      break;
    case 'sun':
      dayString = 'Sunday';
      break;
    default:
      dayString = '';
      break;
  }
  return plural ? `${dayString}s` : dayString;
};

const DAYS = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'] as const;

export const convertShortDayToIndex = (day: ShortDay) => DAYS.indexOf(day);

export const convertIndexToShortDay = (index: number) => DAYS[index];

export const isEndDateAcrossDateline = (
  startDate: string | Date,
  endDate: string | Date,
  timezone: string,
) => {
  const timezoneToUse = USE_DEFAULT_TIMEZONE_FOR_DATELINE_CHECK
    ? DEFAULT_TIMEZONE
    : timezone;
  const start = formatInTimeZone(
    new Date(startDate),
    timezoneToUse,
    'yyyy-MM-dd',
  );
  const end = formatInTimeZone(new Date(endDate), timezoneToUse, 'yyyy-MM-dd');
  return start !== end;
};

export const removeWhitespace = (input: string) => input.replace(/\s+/g, '');

export const convertOnschedHoursToDate = (time: number) => {
  let hours = 0;
  let minutes = 0;

  if (time === 0) {
    hours = 0;
    minutes = 0;
  } else if (time < 1000) {
    const timeString = time.toString();
    const hoursString = timeString.charAt(0);
    const minutesString = timeString.slice(-2);

    hours = Number(hoursString);
    minutes = Number(minutesString);
  } else {
    const timeString = time.toString();
    const hoursString = timeString.slice(0, 2);
    const minutesString = timeString.slice(-2);

    hours = Number(hoursString);
    minutes = Number(minutesString);
  }

  const date = new Date();
  date.setHours(hours);
  date.setMinutes(minutes);

  return date;
};

export const convertDateToOnschedHours = (date: Date) => {
  const hours = date.getHours();
  const minutes = date.getMinutes();

  const hourString = hours.toString();
  let minuteString = minutes.toString();

  if (minuteString.length === 1) {
    minuteString = `0${minuteString}`;
  }

  const combinedString = hourString + minuteString;
  const timeObject = Number(combinedString);

  return timeObject;
};

export const getTimezoneOffsetString = (
  timezone: string,
  dateToCheck?: Date,
): string => {
  // Get the offset in milliseconds
  let offsetMs = getTimezoneOffset(timezone, dateToCheck);
  if (Number.isNaN(offsetMs)) {
    offsetMs = getTimezoneOffset(
      Intl.DateTimeFormat().resolvedOptions().timeZone,
      dateToCheck,
    );
  }

  // Convert milliseconds to minutes
  const offsetMinutes = Math.floor(offsetMs / 60000);

  // Determine the sign
  const offsetSign = offsetMinutes >= 0 ? '+' : '-';

  // Get absolute value of offset in minutes
  const absOffsetMinutes = Math.abs(offsetMinutes);

  // Calculate hours and minutes
  const offsetHours = Math.floor(absOffsetMinutes / 60);
  const offsetRemainingMinutes = absOffsetMinutes % 60;

  // Format hours and minutes with leading zeros if necessary
  const formattedHours = String(offsetHours).padStart(2, '0');
  const formattedMinutes = String(offsetRemainingMinutes).padStart(2, '0');

  // Return formatted offset string
  return `GMT${offsetSign}${formattedHours}${formattedMinutes}`;
};

export const getTimezoneOffsetStringFromMinutes = (offsetMinutes: number) => {
  // Determine the sign
  const offsetSign = offsetMinutes >= 0 ? '+' : '-';

  // Get absolute value of offset in minutes
  const absOffsetMinutes = Math.abs(offsetMinutes);

  // Calculate hours and minutes
  const offsetHours = Math.floor(absOffsetMinutes / 60);
  const offsetRemainingMinutes = absOffsetMinutes % 60;

  // Format hours and minutes with leading zeros if necessary
  const formattedHours = String(offsetHours).padStart(2, '0');
  const formattedMinutes = String(offsetRemainingMinutes).padStart(2, '0');

  // Return formatted offset string
  return `GMT${offsetSign}${formattedHours}${formattedMinutes}`;
};

/**
 *
 * Convert an onsched time to a Date object from the specified timezone to the user's timezone
 * @param time - time in onsched format (e.g. 900 for 9:00 AM)
 * @param timezone - timezone string (e.g. 'America/New_York', 'US/Pacific')
 * @param referenceDate - reference date to use for the timezone conversion. main use is to detect daylight savings time
 * @returns - Date object in the specified timezone
 */
export const convertOnschedHoursToDateFromTimezone = (
  time: number,
  timezone: string,
  referenceDate?: Date,
) => {
  let hours = String(Math.floor(time / 100));
  let minutes = String(time % 100);
  if (hours.length < 2) hours = `0${hours}`;
  if (minutes.length < 2) minutes = `0${minutes}`;

  const now = new Date();
  const dayString = format(now, 'MMMM dd yyyy');
  const timezoneOffset = getTimezoneOffsetString(timezone, referenceDate);

  const dateString = `${dayString} ${hours}:${minutes}:00 ${timezoneOffset}`;

  const dateInTimezone = new Date(dateString);
  return dateInTimezone;
};

/**
 *
 * Convert an onsched time to a Date object using the specified timezone offset to the user's timezone
 * @param time - time in onsched format (e.g. 900 for 9:00 AM)
 * @param timezoneOffset - timezone offset in minutes
 * @returns - Date object in the specified timezone
 */
export const convertOnschedHoursToDateFromOffset = (
  time: number,
  timezoneOffsetMinutes: number,
) => {
  let hours = String(Math.floor(time / 100));
  let minutes = String(time % 100);
  if (hours.length < 2) hours = `0${hours}`;
  if (minutes.length < 2) minutes = `0${minutes}`;

  const now = new Date();
  const dayString = format(now, 'MMMM dd yyyy');
  const timezoneOffset = getTimezoneOffsetStringFromMinutes(
    timezoneOffsetMinutes,
  );

  const dateString = `${dayString} ${hours}:${minutes}:00 ${timezoneOffset}`;

  const dateInTimezone = new Date(dateString);
  return dateInTimezone;
};

export function important<T>(value: T, isImportant = true): T {
  return `${value}${isImportant ? ' !important' : ''}` as any;
}

export function cleanupFirebaseURL(url: string) {
  return url.replace(
    // mobile uploads look like this - works but causes issues when deleting/updating
    'https://firebasestorage.googleapis.com:443/',
    'https://firebasestorage.googleapis.com/',
  );
}

export function cleanupFileName(fileName: string): string {
  // Remove all invalid characters
  const cleanedFileName = fileName.replace(/[^a-zA-Z0-9._-]/g, '');

  // Split the filename into parts using the dot as separator
  const parts = cleanedFileName.split('.');

  if (parts.length > 1) {
    // Join all parts except the last one (name part) and remove any dots within the name
    const name = parts.slice(0, -1).join('').replace(/\./g, '');

    // Get the last part as the extension
    const extension = parts[parts.length - 1];

    // Return the cleaned name with a single dot before the extension
    return `${name}.${extension}`;
  }

  // Return the cleaned name if there's no extension
  return cleanedFileName.replace(/\./g, '');
}

export function getExtension(file: File) {
  const splitFileType = file.type.split('/');
  const splitFileName = file.name.split('.');
  return (
    splitFileType[splitFileType.length - 1] ||
    splitFileName[splitFileName.length - 1]
  );
}

export function isValidFileType(file: File, validTypes: string[]) {
  return validTypes.some(
    acceptedType =>
      // .pdf === pdf
      acceptedType.replace('.', '') === file.type.split('/')[1] ||
      // image/png === image/png
      acceptedType === file.type ||
      // image/* === image/png, image/jpeg, etc.
      (acceptedType.endsWith('/*') &&
        acceptedType.split('/')[0] === file.type.split('/')[0]),
  );
}

export function mergeRefs<T = any>(
  refs: Array<
    React.MutableRefObject<T> | React.LegacyRef<T> | undefined | null
  >,
): React.RefCallback<T> {
  return value => {
    refs.forEach(ref => {
      if (typeof ref === 'function') {
        ref(value);
      } else if (ref != null) {
        // eslint-disable-next-line no-param-reassign
        (ref as React.MutableRefObject<T | null>).current = value;
      }
    });
  };
}

export function formatAddress(
  parts: (string | null | undefined)[],
  newLine = false,
) {
  const filteredParts = parts.filter(Boolean);
  if (newLine) {
    return filteredParts.join('\n');
  }

  return filteredParts.join(', ');
}

export function isNil(value: any) {
  return value === null || value === undefined;
}

export function pluralize(word: string, count: number) {
  return count === 1 ? word : `${word}s`;
}

export function sxToObject(sx: SxProps<Theme> | undefined, theme: Theme) {
  return typeof sx === 'function' ? sx(theme) : sx;
}

export function accessPropertyByString(obj: Record<string, any>, path: string) {
  return path.split('.').reduce((acc, part) => acc?.[part], obj);
}

export function formatPhone(phoneNumber: string) {
  const parsedPhoneNumber = parsePhoneNumber(phoneNumber);
  const formattedPhoneNumber =
    parsedPhoneNumber?.countryCallingCode === '1'
      ? parsedPhoneNumber.formatNational()
      : parsedPhoneNumber.formatInternational();

  return formattedPhoneNumber;
}

export function convertSnakeToTitleCase(snakeCaseStr: string): string {
  return snakeCaseStr
    .toLowerCase() // Convert the entire string to lowercase
    .replace(/_/g, ' ') // Replace underscores with spaces
    .replace(/\b\w/g, char => char.toUpperCase()); // Capitalize the first letter of each word
}

export function formatDateForDateTimeLocal(date: Date): string {
  return format(date, "yyyy-MM-dd'T'HH:mm");
}

// Phone inputs store bare digits; APIs validate against E.164.
export function toE164(value: string | undefined) {
  return !value?.startsWith('+') ? `+${value}` : value;
}

export function testPhoneNumbervalue(value: string | undefined) {
  const num = toE164(value);
  return !!num && isValidPhoneNumber(num);
}

export function limitDecimals(value: number | string, decimals: number) {
  return Number(Number(value).toFixed(decimals));
}

export function getShortRelativeTime(dateToCompate: Date) {
  const now = new Date();

  if (differenceInSeconds(now, dateToCompate) < 60) {
    return `${differenceInSeconds(now, dateToCompate)}s`;
  }
  if (differenceInMinutes(now, dateToCompate) < 60) {
    return `${differenceInMinutes(now, dateToCompate)}m`;
  }
  if (differenceInHours(now, dateToCompate) < 24) {
    return `${differenceInHours(now, dateToCompate)}h`;
  }
  return `${differenceInDays(now, dateToCompate)}d`;
}

export function dedupeList<T>(array: T[]) {
  return [...new Set(array)];
}

export function validateSchema(schema: yup.AnySchema<any, any>, data: any) {
  try {
    schema.validateSync(data);
    return { isValid: true, errors: [] };
  } catch (error) {
    const err = error as yup.ValidationError;
    return { isValid: false, errors: err.errors };
  }
}

export function isReactNativeWebView() {
  return (
    typeof window !== 'undefined' &&
    !!window.ReactNativeWebView &&
    typeof window.ReactNativeWebView.postMessage === 'function'
  );
}

export function postMessageToReactNative(message: string) {
  if (!isReactNativeWebView()) return;
  window.ReactNativeWebView!.postMessage(message);
}

export const testMaxDecimalPlaces = (value: number, max: number): boolean => {
  const [, decPart] = value.toString().split('.');
  if (!decPart || decPart.length <= max) return true;
  return false;
};

export const roundToMaxDecimalPlaces = (value: number, max: number): number => {
  const num = +`1${'0'.repeat(max)}`;
  return Math.round(value * num) / num;
};

export function noop() {}

// iPadOS reports itself as MacIntel, so also check for touch support.
export function isIosDevice() {
  return (
    /iPad|iPhone|iPod/.test(navigator.userAgent) ||
    (navigator.userAgent.includes('Macintosh') && navigator.maxTouchPoints > 1)
  );
}

// For URLs whose response has a Content-Disposition: attachment header
// (e.g. signed export URLs). Same-tab navigation downloads the file without
// leaving the page, so popup blockers never get involved — unlike window.open.
export function downloadFileUrl(url: string) {
  const link = document.createElement('a');
  link.href = url;
  link.rel = 'noopener noreferrer';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
}

export function openExternalUrl(url: string | URL, redirectIfBlocked = true) {
  const isPopupBlocked = window.open(url, '_blank') === null;
  if (!isPopupBlocked) return;

  if (redirectIfBlocked) {
    window.location.href = url.toString();
  } else {
    toast.warning(
      'Your browser is blocking popups. Please allow popups to open this link',
      {
        autoClose: 10_000,
      },
    );
  }
}

export function cleanCents(cents: number) {
  return Math.round(cents);
}

export function getFullTattooLocation(
  tattooLocation?: string | null,
  bodySide?: string | null,
): string {
  if (bodySide && tattooLocation) {
    return `${bodySide} ${tattooLocation}`;
  }
  return tattooLocation || '';
}

export function isMinor(
  date: Date | string | undefined | null,
  compareDate?: Date | string | null,
) {
  if (!date) return false;
  const dateToCompare = compareDate || new Date();
  return differenceInYears(new Date(dateToCompare), new Date(date)) < 18;
}

/** Seconds as m:ss, for anything that counts down or plays. */
export function formatMinutesSeconds(totalSeconds: number) {
  const whole = Math.max(0, Math.floor(totalSeconds));
  const minutes = Math.floor(whole / 60);
  const seconds = whole % 60;
  return `${minutes}:${seconds.toString().padStart(2, '0')}`;
}
